import { NavigateFunction } from 'react-router-dom';
import { BiometryType } from '@capgo/capacitor-native-biometric';
import { isMobile } from 'react-device-detect';
import Resizer from 'react-image-file-resizer';
import { Question, VisibleCondition } from '../models/QuestionModels';
import { DashboardTabItems, FlightPlanSections, ItemIcons, Sections } from './enums';
import {
	getQuestionAnswerFromLocalStorage,
	getSurveyUniqueId,
	setAgeGroupToLocalStorage,
	setClientUniqueId,
	setIsBusinessOwnerToLocalStorage,
	setQuestionAnswerToLocalStorage,
	setSurveyUniqueId
} from './localStorage';
import { getSurveyAnswer } from './api';
import SurveyAnswer from '../models/SurveyAnswer';
import { bbocQuestionsPageUrl, dashboardPageUrl, tabMenuUrlParam, toolsForMobile, toolsForWeb } from './constants';
import ClientAndSurveyResult from '../models/ClientAndSurveyResult';
import { appHeaderHeight, contentPadding, tabletPortraitMinScreenWidth } from '../appComponents/cssConstants';
import ColumnInterface from '../models/ColumnInterface';
import { getLocalizedWord, pixelsToNumber, scrollToRef } from '../lib/utils';
import {
	businessTheme,
	developmentTheme,
	estateTheme,
	healthTheme,
	personalTheme,
	wealthTheme
} from '../appComponents/themes/brandingTheme';
import { getBlobStorageRootUrl } from './countryBasedConfig';
import { ApiResultMessageModel } from '../models/ApiResultMessage';
import { SubscriptionPlanDetail } from '../models/FlightPlanComponent';
import GoSomewhereSvg from '../images/GoSomewhere.svg';
import GoGetterSvg from '../images/GoGetter.svg';
import GettingThereSvg from '../images/GettingThere.svg';
import GettingBySvg from '../images/GettingBy.svg';

// Re-export from appType.ts for backward compatibility
export { isMcProgressApp, isBetterOffApp, getCommunityFeedName } from './appType';

export const evaluateVisibility = (visible: VisibleCondition[]) => {
	if (!visible || visible.length === 0) return true; // default to visible

	let isVisible = false;
	visible.forEach(async (x) => {
		let dependentAnswer = getQuestionAnswerFromLocalStorage(x.questionUniqueName);
		// This is to handle imcomplete survey for app users as the localStorage may be cleared.
		if (!dependentAnswer) {
			const surveyUniqueId = getSurveyUniqueId();
			// This only works for questions in the survey. Must get and set "Client Profile Questions" before go to questions page.
			dependentAnswer = (await getSurveyAnswer(surveyUniqueId, x.questionUniqueName))?.answer;
			setQuestionAnswerToLocalStorage(x.questionUniqueName, dependentAnswer);
		}
		if (x.matchedValues.includes(dependentAnswer)) isVisible = true;
	});

	return isVisible;
};

export const getTheme = (section: string) => {
	switch (section) {
		case Sections.Personal:
			return personalTheme;
		case Sections.Business:
			return businessTheme;
		case Sections.Wealth:
			return wealthTheme;
		case Sections.Estate:
			return estateTheme;
		case FlightPlanSections[FlightPlanSections.Health]:
			return healthTheme;
		case FlightPlanSections[FlightPlanSections.Development]:
			return developmentTheme;
		default:
			throw new Error(`Unknown question section: ${section}`);
	}
};

export const getNextQuestionId = (answers: SurveyAnswer[], questionsData: Question[]) => {
	if (!answers || answers.length === 0) return 1;

	const lastAnsweredQuestionUniqueName = answers[answers.length - 1].questionUniqueName;
	const questionSpec = questionsData.find((x) => x.uniqueName === lastAnsweredQuestionUniqueName);
	const lastQuestionId = questionsData.indexOf(questionSpec) + 1; // questionId in url is the question index + 1 in questionsData
	return lastQuestionId + 1;
};

export const setDependentFieldsAndRedirectToQuestionPage = (
	clientAndSurvey: ClientAndSurveyResult,
	questionId: number,
	navigate: NavigateFunction
) => {
	setClientUniqueId(clientAndSurvey.clientUniqueId);
	setSurveyUniqueId(clientAndSurvey.surveyUniqueId);
	setAgeGroupToLocalStorage(clientAndSurvey.ageGroup);
	setIsBusinessOwnerToLocalStorage(JSON.stringify(clientAndSurvey.isBusinessOwner));
	setTimeout(() => navigate(`${bbocQuestionsPageUrl}/${questionId}`), 0);
};

export const getProfileCompleteStatus = (percentage?: number) => {
	if (!percentage || percentage === 0) return 'Not started';
	if (percentage === 100) return 'Complete';
	return `${percentage}% Complete`;
};

// Questions with N/A answer have null in score, so it will be excluded in average score calculation.
export const calculateOverallScore = (surveyAnswers: SurveyAnswer[]) => {
	const allQuestionAnswers = surveyAnswers?.filter((answer) => answer.score !== null);
	if (!allQuestionAnswers || allQuestionAnswers.length === 0) return null;
	return calculateScore(allQuestionAnswers);
};

export const calculateScore = (surveyAnswers: SurveyAnswer[]) => {
	const totalScores = surveyAnswers.reduce((total, current) => total + current.score, 0);
	return Math.floor(totalScores / surveyAnswers.length);
};

// This function hides all columns for mobile (600px)
export const updateVisibleColumns = (
	initialColumns: ColumnInterface[],
	currrentColumns: ColumnInterface[],
	screenWidth: number,
	setColumns: React.Dispatch<React.SetStateAction<ColumnInterface[]>>
) => {
	const visibleColumns = initialColumns.filter(
		(c: ColumnInterface) => (c.minGridWidth ?? tabletPortraitMinScreenWidth) <= screenWidth
	);
	if (visibleColumns.length !== currrentColumns.length) {
		setColumns(visibleColumns);
	}
};

export const updateVisibleColumnsMemo = (initialColumns: ColumnInterface[], screenWidth: number) =>
	initialColumns.filter((c) => (c.minGridWidth ?? tabletPortraitMinScreenWidth) <= screenWidth);

export const scrollToPageTitleRef = (pageTitleRef: React.RefObject<{ offsetTop: number }>) => {
	scrollToRef(pageTitleRef, pixelsToNumber(appHeaderHeight) + pixelsToNumber(contentPadding));
};

export const getBiometricTypeText = (type: BiometryType) => {
	switch (type) {
		case BiometryType.FACE_ID:
			return 'Face ID';
		case BiometryType.TOUCH_ID:
			return 'Touch ID';
		case BiometryType.FINGERPRINT:
			return 'Fingerprint';
		case BiometryType.FACE_AUTHENTICATION:
			return 'Face Authentication';
		default:
			return 'Biometric Authentication';
	}
};

// TODO: Check this feature request to set default zoom to fitToWidth: https://github.com/telerik/kendo-react/issues/1549
export const defaultPdfViewerZoom = (screenWidth: number) =>
	screenWidth >= 1050 ? 1.25 : Number((screenWidth / 920).toFixed(2));

export const getPdfViewerTools = () => (isMobile ? toolsForMobile : toolsForWeb);

export const getClientProfilePhotoUrl = (fileName?: string) =>
	fileName ? `${getBlobStorageRootUrl()}clientphotos/${fileName}` : '';

export const getClientProfilePhotoUrlPerCountry = (fileName?: string, profilePhotoVersion?: number | null) =>
	fileName ? `${getBlobStorageRootUrl()}clientphotos/${fileName}?v=${profilePhotoVersion ?? 0}` : '';

export const defaultModalMessage: ApiResultMessageModel = { title: '', description: [] };

export const resizeFile = (file: File | Blob, outputType: 'base64' | 'blob' | 'file', isProfilePhoto = false) =>
	new Promise((resolve) => {
		Resizer.imageFileResizer(
			file,
			isProfilePhoto ? 400 : 2000,
			isProfilePhoto ? 400 : 2000,
			'jpeg',
			isProfilePhoto ? 100 : 80,
			0,
			(uri) => {
				resolve(uri);
			},
			outputType
		);
	});

// example dateRange = "7th - 9th May, 2025";
export const getEventStartDate = (dateRange: string): Date | null => {
	const dateRegex = /^(\d{1,2})(?:st|nd|rd|th)?(?: - \d{1,2}(?:st|nd|rd|th)?)? (\w+),? (\d{4})$/;
	const match = dateRange.match(dateRegex);

	if (!match) return null;

	const [, day, month, year] = match;
	const monthIndex = monthMap[month];
	if (monthIndex === undefined) return null;
	return new Date(Number(year), monthIndex, Number(day)); // Can't use 'new Date('2025 March 20')' in Safari
};

// Map month names to index (0 = Jan, 1 = Feb, etc.)
const monthMap: Record<string, number> = {
	January: 0,
	February: 1,
	March: 2,
	April: 3,
	May: 4,
	June: 5,
	July: 6,
	August: 7,
	September: 8,
	October: 9,
	November: 10,
	December: 11
};

export const dashboardPageUrlWithTab = (tab: DashboardTabItems) => `${dashboardPageUrl}?${tabMenuUrlParam}=${tab}`;

const dedicatedContact = 'Dedicated contact';
const emailTelephoneSupport = 'Email and phone support';
const clientProfile = 'Client profile';
const advisoryMeetings = 'Advisory meetings';
const initialTaxLegalReview = 'Initial tax legal review';
const annualFinanceReview = 'Annual finance (mortgage) review';
const annualInvestmentsReview = 'Annual investment (wealth) review';
const annualInsuranceReview = 'Annual insurance review';
const progress12ProgramAccess = 'Access to Progress 12 Program';
const invitationToExclusiveNetworkingEvents = 'Invitation to exclusive networking events';
const invitationToBeBetterOffShowLiveEvents = 'Invites to Be Better Off Show live events';
const digitalATODocumentCorrespondence = `Digital ${getLocalizedWord('taxOffice')} Document Correspondence`;
const secureDigitalDocumentPortal = 'Secure Digital Document Portal';
const bboc = 'Be Better Off Challenge';
const flightPlan = 'Financial Flight Plan';

export const subscriptionPlanDetails: SubscriptionPlanDetail[] = [
	{
		planId: '1',
		planName: 'Going Somewhere',
		icon: GoSomewhereSvg,
		iconAlt: `${ItemIcons.GoSomewhere} icon`,
		subTitle: 'The ultimate Kelly+Partners experience.',
		priceDetail: '$103,920', // TODO: Need to match with Stripe product price, consider getting it from Stripe
		details: [
			{ name: dedicatedContact, value: true },
			{ name: emailTelephoneSupport, value: '24 hour response' },
			{ name: clientProfile, value: 'Complete digital profile' },
			{ name: advisoryMeetings, value: 'Monthly' },
			{ name: initialTaxLegalReview, value: true },
			{ name: annualFinanceReview, value: true },
			{ name: annualInvestmentsReview, value: true },
			{ name: annualInsuranceReview, value: true },
			{ name: progress12ProgramAccess, value: true },
			{ name: invitationToExclusiveNetworkingEvents, value: true },
			{ name: invitationToBeBetterOffShowLiveEvents, value: '2' },
			{ name: secureDigitalDocumentPortal, value: true },
			{ name: digitalATODocumentCorrespondence, value: true },
			{ name: bboc, value: true },
			{ name: flightPlan, value: true }
		]
	},
	{
		planId: '2',
		planName: 'Go Getter',
		icon: GoGetterSvg,
		iconAlt: `${ItemIcons.GoGetter} icon`,
		subTitle: 'The premium Kelly+Partners experience.',
		priceDetail: '$33,450',
		details: [
			{ name: dedicatedContact, value: true },
			{ name: emailTelephoneSupport, value: '24 hour response' },
			{ name: clientProfile, value: 'Complete digital profile' },
			{ name: advisoryMeetings, value: 'Quarterly' },
			{ name: initialTaxLegalReview, value: false },
			{ name: annualFinanceReview, value: true },
			{ name: annualInvestmentsReview, value: true },
			{ name: annualInsuranceReview, value: true },
			{ name: progress12ProgramAccess, value: true },
			{ name: invitationToExclusiveNetworkingEvents, value: true },
			{ name: invitationToBeBetterOffShowLiveEvents, value: '1' },
			{ name: secureDigitalDocumentPortal, value: true },
			{ name: digitalATODocumentCorrespondence, value: true },
			{ name: bboc, value: true },
			{ name: flightPlan, value: true }
		]
	},
	{
		planId: '3',
		planName: 'Getting There',
		icon: GettingThereSvg,
		iconAlt: `${ItemIcons.GettingThere} icon`,
		subTitle: 'The tailored Kelly+Partners experience.',
		priceDetail: '$23,000',
		details: [
			{ name: dedicatedContact, value: true },
			{ name: emailTelephoneSupport, value: '48 hour response' },
			{ name: clientProfile, value: 'Basic profile' },
			{ name: advisoryMeetings, value: 'Quarterly' },
			{ name: initialTaxLegalReview, value: false },
			{ name: annualFinanceReview, value: true },
			{ name: annualInvestmentsReview, value: false },
			{ name: annualInsuranceReview, value: true },
			{ name: progress12ProgramAccess, value: true },
			{ name: invitationToExclusiveNetworkingEvents, value: true },
			{ name: invitationToBeBetterOffShowLiveEvents, value: false },
			{ name: secureDigitalDocumentPortal, value: true },
			{ name: digitalATODocumentCorrespondence, value: true },
			{ name: bboc, value: true },
			{ name: flightPlan, value: true }
		]
	},
	{
		planId: '4',
		planName: 'Getting By',
		icon: GettingBySvg,
		iconAlt: `${ItemIcons.GettingBy} icon`,
		subTitle: 'The advanced Kelly+Partners experience.',
		priceDetail: '$12,500',
		details: [
			{ name: dedicatedContact, value: true },
			{ name: emailTelephoneSupport, value: '72 hour response' },
			{ name: clientProfile, value: 'Basic profile' },
			{ name: advisoryMeetings, value: 'Bi-Annual' },
			{ name: initialTaxLegalReview, value: false },
			{ name: annualFinanceReview, value: false },
			{ name: annualInvestmentsReview, value: false },
			{ name: annualInsuranceReview, value: false },
			{ name: progress12ProgramAccess, value: true },
			{ name: invitationToExclusiveNetworkingEvents, value: true },
			{ name: invitationToBeBetterOffShowLiveEvents, value: false },
			{ name: secureDigitalDocumentPortal, value: true },
			{ name: digitalATODocumentCorrespondence, value: true },
			{ name: bboc, value: true },
			{ name: flightPlan, value: true }
		]
	}
];
